import { translate, TranslationEntity } from '@repo/utils';
import { ALL_DATA_TYPES } from 'advanced-filters/data';
import {
  Flex, Form, InputNumber, Select, Typography,
} from 'antd';
import { taskScheduleUnitOptions } from 'automations/data/actionNodes';
import useMailMergeFields from 'automations/hooks/useMailMergeFields';
import { type CreateTaskNodeData } from 'automations/types/nodeSchemas/actionNodes';
import { type TaskTemplateListType } from 'crm-atoms/index';
import { useMemo } from 'react';

import styles from './CreateTaskContent.module.scss';

type CreateTaskContentType = CreateTaskNodeData['additionalOptions']['createTask'];

const AUTOMATION_EXECUTION_TIME = 'Automation Execution Time';

export default function ModifyScheduleForm({
  taskTemplateData,
}: {
  taskTemplateData?: TaskTemplateListType
}) {
  const form = Form.useFormInstance<CreateTaskContentType>();
  const taskTemplateId = Form.useWatch('templateId', form);
  const dependsOnValue = Form.useWatch(['schedule', 'dependsOn'], form);

  const {
    options: mailMergeOptions,
  } = useMailMergeFields({
    allowedDataTypes: [ALL_DATA_TYPES.datetime, ALL_DATA_TYPES.date],
  });

  const taskTemplateType = useMemo(() => {
    if (!taskTemplateId) return null;
    return taskTemplateData?.taskTemplates.find((template) => template.id === taskTemplateId.toString())?.taskType;
  }, [taskTemplateData, taskTemplateId]);

  const finalOptions = useMemo(() => [
    { label: AUTOMATION_EXECUTION_TIME, value: AUTOMATION_EXECUTION_TIME },
    ...mailMergeOptions,
  ], [mailMergeOptions]);

  if (!taskTemplateType) return null;

  if (taskTemplateType === 'appointment') {
    return (
      <Flex gap={8} align="center">
        <Form.Item
          name={['schedule', 'value']}
          rules={[{ required: true, message: 'Schedule is required' }]}
          initialValue={1}
        >
          <InputNumber min={1} />
        </Form.Item>
        <Form.Item
          name={['schedule', 'unit']}
          initialValue="days"
          rules={[{ required: true, message: 'Schedule Unit is required' }]}
        >
          <Select
            options={taskScheduleUnitOptions}
            className={styles.taskScheduleSelectOptions}
            allowClear
          />
        </Form.Item>
        <Form.Item
          name={['schedule', 'isAfter']}
          initialValue
          rules={[{ required: true, message: 'Schedule is required' }]}
        >
          <Select
            options={[
              { label: 'After', value: true },
              { label: 'Before', value: false, disabled: dependsOnValue === AUTOMATION_EXECUTION_TIME },
            ]}
            className={styles.taskScheduleSelectOptions}
          />
        </Form.Item>
        <Form.Item
          name={['schedule', 'dependsOn']}
          rules={[{ required: true, message: 'Depends On is required' }]}
        >
          <Select
            options={finalOptions}
            className={styles.taskScheduleDependsOnOptions}
            placeholder="Depends On"
          />
        </Form.Item>
      </Flex>
    );
  } else {
    return (
      <Typography.Text type="secondary">
        {
          translate(
            `Schedules are Only Configured for Appointment #{${TranslationEntity.task}}`,
            [{ capitalize: true, pluralize: true }],
          )
        }
      </Typography.Text>
    );
  }
}
